import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Edit,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  AlertTriangle,
  Upload,
  Settings2,
} from "lucide-react";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { queryClient } from "@/ajax/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { OrgFormForm, OrgFormFormValues } from "./org-form-form";
import { PdfFormUploadDialog } from "./pdf/pdf-form-upload-dialog";
import { PdfFormBuilderDialog } from "./pdf/pdf-form-builder-dialog";
import { toast } from "sonner";
import { FormFieldType } from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";

type OrgForm = RouterOutputs["organizationForm"]["listForms"][number];
type OrgFormWithFields = RouterOutputs["organizationForm"]["getForm"];

const FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  [FormFieldType.Text]: "Short Text",
  [FormFieldType.Textarea]: "Long Text",
  [FormFieldType.Select]: "Single Select",
  [FormFieldType.MultiSelect]: "Multi Select",
  [FormFieldType.FileUpload]: "File Upload",
  [FormFieldType.Date]: "Date",
  [FormFieldType.Checkbox]: "Checkbox",
  [FormFieldType.Signature]: "Signature",
};

function PdfMappingStatusBadge({
  status,
}: {
  status: string | null | undefined;
}) {
  if (!status) {
    return null;
  }
  if (status === "ready") {
    return (
      <Badge
        variant="outline"
        className="bg-green-50 text-green-700 border-green-200 text-xs h-5 px-1.5"
      >
        Ready
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge
        variant="outline"
        className="bg-red-50 text-red-700 border-red-200 text-xs h-5 px-1.5"
      >
        Mapping Error
      </Badge>
    );
  }
  // draft or any other status
  return (
    <Badge
      variant="outline"
      className="bg-amber-50 text-amber-700 border-amber-200 text-xs h-5 px-1.5"
    >
      Draft
    </Badge>
  );
}

function FormRow({
  orgForm,
  organizationBusinessUnitId,
  enableDocumentSigning,
  onEdit,
  onDelete,
  onRemapPdf,
}: {
  orgForm: OrgForm;
  organizationBusinessUnitId: string;
  enableDocumentSigning: boolean;
  onEdit: (form: OrgForm) => void;
  onDelete: (form: OrgForm) => void;
  onRemapPdf: (form: OrgForm) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const trpc = useTRPC();

  const { data: formDetails } = useQuery(
    trpc.organizationForm.getForm.queryOptions(
      {
        organizationBusinessUnitId,
        id: orgForm.id,
      },
      { enabled: isExpanded },
    ),
  );

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-white hover:bg-gray-50 transition-colors">
        <button
          onClick={() => setIsExpanded((prev) => !prev)}
          className="text-gray-400 hover:text-gray-600 shrink-0"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 text-sm sm:text-base">
              {orgForm.name}
            </span>
            {orgForm.templateKind === "file" && (
              <Badge
                variant="outline"
                className="bg-purple-50 text-purple-700 border-purple-200 text-xs h-5 px-1.5"
              >
                PDF
              </Badge>
            )}
            {orgForm.templateKind === "file" && (
              <PdfMappingStatusBadge status={orgForm.pdfMappingStatus} />
            )}
          </div>
          {orgForm.description && (
            <p className="text-sm text-gray-500 mt-0.5 truncate">
              {orgForm.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {orgForm.templateKind === "file" && enableDocumentSigning && (
            <Button
              variant="ghost"
              size="sm"
              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-8 px-2"
              onClick={() => onRemapPdf(orgForm)}
              title="Remap PDF fields"
            >
              <Settings2 size={14} className="sm:mr-1" />
              <span className="hidden sm:inline">Remap</span>
            </Button>
          )}
          {orgForm.templateKind !== "file" && (
            <Button
              variant="ghost"
              size="sm"
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 px-2"
              onClick={() => onEdit(orgForm)}
            >
              <Edit size={14} className="sm:mr-1" />
              <span className="hidden sm:inline">Edit</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 px-2"
            onClick={() => onDelete(orgForm)}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
          {!formDetails ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-8 bg-gray-200 rounded animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {formDetails.fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-start gap-3 text-sm py-1.5 border-b border-gray-100 last:border-0"
                >
                  <span className="text-gray-400 text-xs font-mono mt-0.5 w-5 text-right shrink-0">
                    {index + 1}.
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-gray-800">{field.label}</span>
                      {!field.isRequired && (
                        <span className="text-xs text-gray-400">
                          (optional)
                        </span>
                      )}
                    </div>
                    {field.description && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {field.description}
                      </p>
                    )}
                    {field.options && field.options.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {field.options.map((opt, i) => (
                          <Badge
                            key={i}
                            variant="outline"
                            className="text-xs h-4 px-1.5 bg-white"
                          >
                            {opt}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-blue-50 text-blue-700 border-blue-200 text-xs shrink-0"
                  >
                    {FIELD_TYPE_LABELS[field.fieldType as FormFieldType]}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FormDeleteDialog({
  form: deletingForm,
  organizationBusinessUnitId,
  onOpenChange,
  onConfirm,
}: {
  form: OrgForm | null;
  organizationBusinessUnitId: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const trpc = useTRPC();
  const { data: usage, isLoading } = useQuery(
    trpc.organizationForm.getFormUsage.queryOptions(
      {
        organizationBusinessUnitId,
        id: deletingForm?.id ?? "",
      },
      { enabled: !!deletingForm },
    ),
  );

  const isInUse = (usage?.templates.length ?? 0) > 0;

  return (
    <Dialog open={!!deletingForm} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] sm:max-h-[90vh] w-[95vw] sm:w-full">
        <DialogHeader>
          <DialogTitle>Delete Form</DialogTitle>
          {isLoading ? (
            <DialogDescription>Checking usage...</DialogDescription>
          ) : isInUse ? (
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">This form is in use</p>
                  <p className="mt-1">
                    Remove it from the following hiring process
                    {usage!.templates.length > 1
                      ? " templates"
                      : " template"}{" "}
                    before deleting:
                  </p>
                  <ul className="mt-1.5 list-disc pl-4 space-y-0.5">
                    {usage!.templates.map((t) => (
                      <li key={t.templateId} className="font-medium">
                        {t.templateName}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deletingForm?.name}
              &rdquo;? This action cannot be undone.
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            {isInUse ? "Close" : "Cancel"}
          </Button>
          {!isLoading && !isInUse && (
            <Button
              variant="destructive"
              onClick={onConfirm}
              className="w-full sm:w-auto"
            >
              Delete
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OrgFormsPage() {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const buId = selectedOrgBusinessUnit.id;
  const enableDocumentSigning =
    selectedOrgBusinessUnit.enableDocumentSigning === true;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isPdfUploadOpen, setIsPdfUploadOpen] = useState(false);
  const [remappingPdfForm, setRemappingPdfForm] = useState<OrgForm | null>(
    null,
  );
  const [editingForm, setEditingForm] = useState<OrgForm | null>(null);
  const [editingFormDetails, setEditingFormDetails] =
    useState<OrgFormWithFields | null>(null);
  const [deletingForm, setDeletingForm] = useState<OrgForm | null>(null);

  const { data: forms, isLoading } = useQuery(
    trpc.organizationForm.listForms.queryOptions({
      organizationBusinessUnitId: buId,
    }),
  );

  React.useEffect(() => {
    if (!enableDocumentSigning) {
      setIsPdfUploadOpen(false);
      setRemappingPdfForm(null);
    }
  }, [enableDocumentSigning]);

  const createMutation = useMutation(
    trpc.organizationForm.createForm.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organizationForm.listForms.queryOptions({
            organizationBusinessUnitId: buId,
          }),
        );
        setIsCreateOpen(false);
        toast.success("Form created");
      },
      onError: (err) => {
        toast.error(err.message || "Failed to create form");
      },
    }),
  );

  const updateMutation = useMutation(
    trpc.organizationForm.updateForm.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organizationForm.listForms.queryOptions({
            organizationBusinessUnitId: buId,
          }),
        );
        if (editingForm) {
          await queryClient.invalidateQueries(
            trpc.organizationForm.getForm.queryOptions({
              organizationBusinessUnitId: buId,
              id: editingForm.id,
            }),
          );
        }
        setEditingForm(null);
        setEditingFormDetails(null);
        toast.success("Form updated");
      },
      onError: (err) => {
        toast.error(err.message || "Failed to update form");
      },
    }),
  );

  const deleteMutation = useMutation(
    trpc.organizationForm.deleteForm.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organizationForm.listForms.queryOptions({
            organizationBusinessUnitId: buId,
          }),
        );
        setDeletingForm(null);
        toast.success("Form deleted");
      },
      onError: (err) => {
        toast.error(err.message || "Failed to delete form");
      },
    }),
  );

  const handleCreate = (values: OrgFormFormValues) => {
    createMutation.mutate({
      organizationBusinessUnitId: buId,
      name: values.name,
      description: values.description ?? null,
      fields: values.fields.map((f) => ({
        ...f,
        description: f.description ?? null,
        options: f.options ?? null,
      })),
    });
  };

  const handleUpdate = (values: OrgFormFormValues) => {
    if (!editingForm) {
      return;
    }
    updateMutation.mutate({
      organizationBusinessUnitId: buId,
      id: editingForm.id,
      name: values.name,
      description: values.description ?? null,
      fields: values.fields.map((f) => ({
        ...f,
        description: f.description ?? null,
        options: f.options ?? null,
      })),
    });
  };

  const handleDelete = () => {
    if (!deletingForm) {
      return;
    }
    deleteMutation.mutate({
      organizationBusinessUnitId: buId,
      id: deletingForm.id,
    });
  };

  const handleEditClick = async (orgForm: OrgForm) => {
    setEditingForm(orgForm);
    const details = await queryClient.fetchQuery(
      trpc.organizationForm.getForm.queryOptions({
        organizationBusinessUnitId: buId,
        id: orgForm.id,
      }),
    );
    setEditingFormDetails(details);
  };

  const editDefaultValues: Partial<OrgFormFormValues> | undefined =
    editingFormDetails
      ? {
          name: editingFormDetails.name,
          description: editingFormDetails.description ?? "",
          fields: editingFormDetails.fields.map((field) => ({
            label: field.label,
            description: field.description ?? "",
            fieldType: field.fieldType as FormFieldType,
            sortOrder: field.sortOrder,
            isRequired: field.isRequired,
            options: field.options ?? [],
          })),
        }
      : undefined;

  return (
    <div className="p-4 sm:p-6 w-full max-w-4xl mx-auto">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-6 w-6 text-blue-600" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Forms
            </h1>
          </div>
          <p className="text-gray-500 text-sm">
            Create and manage forms that providers can fill out as part of
            checklist tasks.
          </p>
        </div>
        <div className="flex gap-2 flex-col sm:flex-row">
          {enableDocumentSigning && (
            <Button
              variant="outline"
              onClick={() => setIsPdfUploadOpen(true)}
              className="w-full sm:w-auto"
            >
              <Upload size={16} className="mr-1.5" />
              Upload PDF Form
            </Button>
          )}
          <Button
            onClick={() => setIsCreateOpen(true)}
            className="w-full sm:w-auto"
          >
            <Plus size={16} className="mr-1.5" />
            New Form
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-3" />
          <p className="text-gray-500 text-sm">Loading forms...</p>
        </div>
      ) : !forms || forms.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-medium text-gray-700 mb-1">
            No forms yet
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Create a form to collect information from providers through
            checklist tasks.
          </p>
          <Button variant="outline" onClick={() => setIsCreateOpen(true)}>
            <Plus size={14} className="mr-1.5" />
            Create First Form
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {forms.map((orgForm) => (
            <FormRow
              key={orgForm.id}
              orgForm={orgForm}
              organizationBusinessUnitId={buId}
              enableDocumentSigning={enableDocumentSigning}
              onEdit={handleEditClick}
              onDelete={setDeletingForm}
              onRemapPdf={setRemappingPdfForm}
            />
          ))}
        </div>
      )}

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] sm:max-h-[90vh] overflow-y-auto !p-0">
          <DialogHeader className="mb-4 pt-6 px-4 sm:px-6">
            <DialogTitle>Create Form</DialogTitle>
          </DialogHeader>
          <OrgFormForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateOpen(false)}
            isSubmitting={createMutation.isPending}
            error={createMutation.error?.message}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editingForm}
        onOpenChange={(open) => {
          if (!open) {
            setEditingForm(null);
            setEditingFormDetails(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] sm:max-h-[90vh] overflow-y-auto !p-0">
          <DialogHeader className="mb-4 pt-6 px-4 sm:px-6">
            <DialogTitle>Edit Form</DialogTitle>
          </DialogHeader>
          {editingFormDetails ? (
            <OrgFormForm
              defaultValues={editDefaultValues}
              onSubmit={handleUpdate}
              onCancel={() => {
                setEditingForm(null);
                setEditingFormDetails(null);
              }}
              isSubmitting={updateMutation.isPending}
              error={updateMutation.error?.message}
            />
          ) : (
            <div className="py-8 text-center text-gray-500">
              Loading form...
            </div>
          )}
        </DialogContent>
      </Dialog>

      <FormDeleteDialog
        form={deletingForm}
        organizationBusinessUnitId={buId}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingForm(null);
          }
        }}
        onConfirm={handleDelete}
      />

      {enableDocumentSigning && (
        <PdfFormUploadDialog
          open={isPdfUploadOpen}
          onOpenChange={setIsPdfUploadOpen}
          organizationBusinessUnitId={buId}
        />
      )}

      {enableDocumentSigning && (
        <PdfFormBuilderDialog
          form={remappingPdfForm}
          organizationBusinessUnitId={buId}
          onOpenChange={(open) => {
            if (!open) {
              setRemappingPdfForm(null);
            }
          }}
        />
      )}
    </div>
  );
}
